import React, { useEffect, useState, useMemo, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { printHtmlSafely } from "@/lib/printHtml";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Printer,
  PackageCheck,
  FileText,
  Loader2,
  AlertTriangle,
} from "lucide-react";

/**
 * PickListDialog — opens for a source shop, shows aggregated pick list,
 * supports browser-print and bulk Mark-Dispatched.
 *
 * Props:
 *   open: bool
 *   onOpenChange: (bool) => void
 *   sourceShopId: string — required when open
 *   itemIds?: string[] — optional subset of restock_queue ids to filter to
 *   onDispatched?: () => void  — called after successful bulk dispatch
 */
export default function PickListDialog({
  open,
  onOpenChange,
  sourceShopId,
  itemIds,
  onDispatched,
}) {
  const [loading, setLoading] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [data, setData] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    if (!open || !sourceShopId) {
      setData(null);
      return;
    }
    setLoading(true);
    api
      .post("/management/pick-list/preview", {
        source_shop_id: sourceShopId,
        item_ids: itemIds && itemIds.length ? itemIds : null,
      })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    // setData/setLoading are stable React setters; api/toast/formatApiError are
    // module-level imports; itemIds is intentionally treated as an array of ids
    // (callers pass a memoized slice) — adding it could cause refetch loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceShopId, itemIds]);

  const handlePrint = () => {
    if (!printRef.current) return;
    const html = `
      <html>
        <head>
          <title>Pick List - ${data?.source_shop?.name || ""}</title>
          <style>
            @page { size: A4; margin: 12mm; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #111; margin: 0; padding: 12px; }
            h1 { font-size: 18px; margin: 0 0 4px; }
            .meta { color: #555; font-size: 11px; margin-bottom: 12px; }
            .summary { display: flex; gap: 16px; padding: 8px 12px; background: #f4eee9; border-radius: 6px; margin-bottom: 12px; font-size: 11px; }
            .summary b { font-size: 14px; color: #9E6D5B; display: block; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th { text-align: left; background: #f4eee9; padding: 6px; border-bottom: 2px solid #9E6D5B; }
            td { padding: 6px; border-bottom: 1px solid #eee; vertical-align: top; }
            .qty { font-family: 'Courier New', monospace; font-weight: bold; text-align: right; }
            .dest { display: inline-block; padding: 1px 6px; background: #e6f4ea; color: #1e5128; border-radius: 3px; margin-right: 4px; font-size: 10px; }
            .batch { display: inline-block; padding: 1px 5px; background: #f0f0f0; border: 1px solid #ddd; border-radius: 3px; margin-right: 3px; font-size: 9px; }
            .urgent { color: #c2410c; font-weight: bold; }
            .signature { margin-top: 32px; display: flex; justify-content: space-between; font-size: 11px; }
            .sig-line { width: 200px; border-top: 1px solid #999; padding-top: 4px; text-align: center; color: #666; }
            .check { width: 16px; height: 16px; border: 1.5px solid #888; display: inline-block; }
            @media print { button { display: none !important; } }
          </style>
        </head>
        <body>${printRef.current.innerHTML}</body>
      </html>
    `;
    printHtmlSafely(html, {
      onError: (err) => {
        console.error("Print failed:", err);
        toast.error("Could not open print dialog");
      },
    });
  };

  const handleDispatchAll = async () => {
    if (
      !window.confirm(
        `Mark all ${data?.lines?.length || 0} item(s) as DISPATCHED?\n\n` +
          `This flips their status — the destination shop will see them in their incoming queue.`
      )
    )
      return;
    setDispatching(true);
    try {
      const { data: res } = await api.post("/management/pick-list/dispatch-all", {
        source_shop_id: sourceShopId,
        item_ids: itemIds && itemIds.length ? itemIds : null,
      });
      toast.success(`Dispatched ${res.dispatched} item(s)`);
      if (onDispatched) onDispatched();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDispatching(false);
    }
  };

  const generatedAt = useMemo(
    () => (data?.generated_at ? formatDateTime(data.generated_at) : ""),
    [data]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="pick-list-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#9E6D5B]" />
            Pick List — Outbound Transfer
          </DialogTitle>
          <DialogDescription>
            Use this list to pull stock off shelves. Print it, tick each line, then click <b>Mark All Dispatched</b>.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-[#9E6D5B]" />
            Building pick list…
          </div>
        ) : !data ? null : data.lines.length === 0 ? (
          <div className="p-6 text-center text-sm text-amber-700 bg-amber-50 rounded border border-amber-200">
            <AlertTriangle className="w-6 h-6 mx-auto mb-2" />
            No active reservations from <strong>{data.source_shop.name}</strong> are awaiting dispatch.
          </div>
        ) : (
          <div ref={printRef}>
            <h1>Pick List — {data.source_shop.name}</h1>
            <div className="meta text-xs text-muted-foreground mb-2">
              Generated {generatedAt} by {data.generated_by || "—"}
            </div>
            <div className="summary mb-3">
              <div>
                <b data-testid="pl-summary-lines">{data.summary.total_lines}</b>line(s)
              </div>
              <div>
                <b data-testid="pl-summary-units">{data.summary.total_units}</b>total units
              </div>
              <div>
                <b data-testid="pl-summary-destinations">{data.summary.total_destinations}</b>destination(s)
              </div>
            </div>
            <table data-testid="pl-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>✓</th>
                  <th>Product</th>
                  <th className="qty" style={{ width: 70 }}>Total</th>
                  <th>Destinations</th>
                  <th>Suggested Batches (FEFO)</th>
                </tr>
              </thead>
              <tbody>
                {data.lines.map((ln) => (
                  <tr key={ln.product_id} data-testid={`pl-row-${ln.product_id}`}>
                    <td><span className="check"></span></td>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {ln.product_snapshot?.product_name}
                      </div>
                      <div style={{ fontSize: 10, color: "#666" }}>
                        {ln.product_snapshot?.brand_name || "—"} ·{" "}
                        {ln.product_snapshot?.auto_barcode || ""}
                      </div>
                    </td>
                    <td className="qty">{ln.total_qty}</td>
                    <td>
                      {ln.destinations.map((d) => (
                        <span
                          key={`${ln.product_id}-${d.destination_shop_id || d.destination_shop_name}`}
                          className={`dest ${d.priority === "urgent" ? "urgent" : ""}`}
                        >
                          {d.destination_shop_name} ×{d.qty}
                          {d.priority === "urgent" ? " ⚠" : ""}
                        </span>
                      ))}
                    </td>
                    <td>
                      {(ln.suggested_batches || []).slice(0, 3).map((b) => (
                        <span key={b.id || `${ln.product_id}-${b.expiry_date || "noexp"}-${b.mrp || 0}`} className="batch">
                          {b.expiry_date || "no exp"} · ₹{b.mrp ?? "?"} · {b.available} on hand
                          {b.days_until_expiry !== null && b.days_until_expiry < 60
                            ? " ⚠"
                            : ""}
                        </span>
                      ))}
                      {(ln.suggested_batches || []).length > 3 && (
                        <span className="batch" key={`${ln.product_id}-more`}>
                          +{ln.suggested_batches.length - 3} more
                        </span>
                      )}
                      {!ln.suggested_batches?.length && (
                        <span style={{ color: "#c2410c", fontSize: 10 }}>
                          ⚠ No matching batches at source
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="signature">
              <div className="sig-line">Picked by</div>
              <div className="sig-line">Verified by</div>
              <div className="sig-line">Date / Time</div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="pl-close"
          >
            Close
          </Button>
          {data && data.lines.length > 0 && (
            <>
              <Button
                variant="outline"
                onClick={handlePrint}
                data-testid="pl-print"
              >
                <Printer className="w-4 h-4 mr-1" />
                Print
              </Button>
              <Button
                disabled={dispatching}
                onClick={handleDispatchAll}
                className="text-white"
                style={{ backgroundColor: "#9E6D5B" }}
                data-testid="pl-dispatch-all"
              >
                {dispatching ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <PackageCheck className="w-4 h-4 mr-1" />
                )}
                Mark All Dispatched ({data.summary.total_lines})
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Badge re-export used by older callers
export { Badge };
